/**
 * Create a channel.
 *
 * Three fields, of which only two are required, because a channel that takes
 * a form to start is a channel nobody starts.
 *
 * The handle is the one with rules, and they are enforced as you type rather
 * than on submit: it is the public address, it cannot be changed afterwards
 * because links will already point at it, and finding that out from a red
 * message after pressing Create is the wrong moment.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { channels as channelService, normaliseHandle, userMessage } from '@zivapp/shared'
import './NewChannel.css'

export default function NewChannel () {
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [handle, setHandle] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const parsed = normaliseHandle(handle)
  // Silent until they have typed something: a validation message on an empty
  // field is a complaint about not having started yet.
  const handleProblem = handle.trim() && !parsed.ok ? parsed.reason : null
  const canCreate = Boolean(name.trim()) && parsed.ok && !busy

  const create = async event => {
    event.preventDefault()
    if (!canCreate) return

    setBusy(true)
    setError(null)
    try {
      const channel = await channelService.create({
        name: name.trim(),
        handle: parsed.handle,
        description: description.trim() || null
      })
      navigate(`/channels/${channel.id}`, { replace: true })
    } catch (cause) {
      setError(userMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="nch">
      <header className="nch__bar">
        <button
          type="button"
          className="nch__back"
          onClick={() => navigate('/updates')}
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5 8 12l7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1 className="nch__title">New channel</h1>
      </header>

      <form className="nch__form" onSubmit={create}>
        <p className="nch__intro">
          A channel is a one-way broadcast. You post; the people who follow
          read. They cannot reply, and they cannot see each other.
        </p>

        <label className="nch__field">
          <span className="nch__label">Channel name</span>
          <input
            className="nch__input"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={100}
            placeholder="What is this channel called?"
            data-testid="channel-name"
            autoFocus
          />
        </label>

        <label className="nch__field">
          <span className="nch__label">Handle</span>
          <span className="nch__handle-wrap">
            <span className="nch__at">@</span>
            <input
              className="nch__input nch__input--handle"
              value={handle}
              onChange={e => setHandle(e.target.value)}
              maxLength={30}
              placeholder="handle"
              data-testid="channel-handle"
              spellCheck="false"
              autoCapitalize="none"
            />
          </span>
          <span className={`nch__hint${handleProblem ? ' is-bad' : ''}`}>
            {handleProblem ??
              'Your channel’s public address. It cannot be changed later, because links will point at it.'}
          </span>
        </label>

        <label className="nch__field">
          <span className="nch__label">Description <span className="nch__optional">optional</span></span>
          <textarea
            className="nch__input nch__textarea"
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="What will you post here?"
          />
        </label>

        {error && <p className="nch__error" role="alert">{error}</p>}

        <button
          type="submit"
          className="nch__create"
          disabled={!canCreate}
          data-testid="create-channel-submit"
        >
          {busy ? 'Creating…' : 'Create channel'}
        </button>
      </form>
    </div>
  )
}
